import { useCallback, useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { type ExportParams } from 'lib/hooks/useServerExport';
import {
  postSpatialExport,
  useSpatialExport,
} from 'lib/hooks/use-spatial-export';
import { runDefaultSearchesAtom, waitingForMapBoundsAtom } from './atoms';

/**
 * The combined spatial-export request body: formats plus per-dataset filter params + attributes.
 *
 * Intersected with `ExportParams` so it carries that type's `[key: string]: unknown` index signature
 * and is assignable to `startExport`'s parameter both ways (a plain object type is not — that mismatch
 * was failing the `types` CI job).
 */
export type SpatialExportRequest = ExportParams & {
  formats: string[];
  datasets: Record<
    string,
    { params: Record<string, unknown>; attributes: string[] }
  >;
};

/**
 * Owns the Map Query combined spatial (GIS) export: POSTs the selected datasets + formats to the
 * combined endpoint and tracks completion over Pusher (auto-downloading the ZIP), through the shared
 * useSpatialExport flow. One export spans every selected dataset.
 *
 * @return {object} The server-export status + `startExport(request)` + the signed-in user id.
 */
export const useMapQuerySpatialExport = () => {
  const exportFn = useCallback(
    (params?: ExportParams) =>
      postSpatialExport('queries/map_query/spatial_export', params),
    []
  );

  return useSpatialExport<SpatialExportRequest>({
    event: 'map_query_export_complete',
    exportFn,
  });
};

export type MapQuerySpatialExport = ReturnType<typeof useMapQuerySpatialExport>;

/**
 * Starts the lists left waiting for the map's first bounds once they stop
 * waiting: the filters or the map are hidden, or the mode changes, first.
 */
export const useSearchWithoutMapBounds = () => {
  const waiting = useAtomValue(waitingForMapBoundsAtom);
  const runDefaultSearches = useSetAtom(runDefaultSearchesAtom);
  useEffect(() => {
    if (!waiting) runDefaultSearches();
  }, [waiting, runDefaultSearches]);
};

/**
 * Tracks a press of Retry until the counts request it waits on settles. A pan or
 * a typed search refetches the counts too, and that isn't a retry.
 * @param {boolean} loading Whether a counts request is in flight.
 * @param {Function} retry Sends the failed counts request again.
 * @return {object} Whether a press is waiting, and the press handler.
 */
export const useFacetsRetry = (
  loading: boolean,
  retry: () => void
): { retrying: boolean; onRetry: () => void } => {
  const [pressed, setPressed] = useState(false);
  if (pressed && !loading) setPressed(false);
  const onRetry = useCallback(() => {
    if (!loading) retry();
    setPressed(true);
  }, [loading, retry]);
  return { retrying: pressed, onRetry };
};
